import { CONTACT_REQUIREMENT_LABELS, CONTACT_REQUIREMENTS, type ResearchConfigInput } from '@shared/config';
import { Alert } from '../../components/ui/feedback';
import { Checkbox, Field, Input, Select } from '../../components/ui/form';

export interface StepRulesProps {
  config: ResearchConfigInput;
  onChange: (patch: Partial<ResearchConfigInput>) => void;
  errors: Record<string, string>;
}

/** Step 4. Contact requirement, enrichment and the merge thresholds. */
export function StepRules({ config, onChange, errors }: StepRulesProps) {
  const rules = config.rules ?? {};
  const enrichment = config.enrichment ?? {};
  const website = enrichment.website ?? {};
  const people = enrichment.people ?? {};

  const patchRules = (patch: Partial<NonNullable<ResearchConfigInput['rules']>>): void => {
    onChange({ rules: { ...rules, ...patch } });
  };
  const patchEnrichment = (patch: Partial<NonNullable<ResearchConfigInput['enrichment']>>): void => {
    onChange({ enrichment: { ...enrichment, ...patch } });
  };

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <div>
          <h3 className="text-[13px] font-semibold text-foreground">Contact requirement</h3>
          <p className="text-[13px] text-muted-foreground">
            Businesses without the required contact details are discarded after enrichment, so a business is only dropped once
            its website has been checked too.
          </p>
        </div>
        <Field label="Keep a business when it has">
          {({ id }) => (
            <Select
              id={id}
              value={rules.contactRequirement ?? 'phone_or_email'}
              onChange={(event) =>
                patchRules({ contactRequirement: event.target.value as NonNullable<typeof rules.contactRequirement> })
              }
            >
              {CONTACT_REQUIREMENTS.map((requirement) => (
                <option key={requirement} value={requirement}>
                  {CONTACT_REQUIREMENT_LABELS[requirement]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Checkbox
          label="Exclude permanently closed businesses"
          checked={rules.excludePermanentlyClosed ?? true}
          onChange={(event) => patchRules({ excludePermanentlyClosed: event.target.checked })}
        />
      </section>

      <section className="space-y-3">
        <div>
          <h3 className="text-[13px] font-semibold text-foreground">Website enrichment</h3>
          <p className="text-[13px] text-muted-foreground">
            Visits each company website to find emails, phones, social profiles and hiring signals that listings omit.
          </p>
        </div>
        <Checkbox
          label="Visit company websites"
          checked={website.enabled ?? true}
          onChange={(event) => patchEnrichment({ website: { ...website, enabled: event.target.checked } })}
        />
        {website.enabled ?? true ? (
          <div className="grid gap-4 pl-6 sm:grid-cols-2">
            <Field label="Pages per site" hint="Homepage, contact, about and careers pages are prioritised.">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  type="number"
                  min={1}
                  max={15}
                  className="tabular"
                  aria-describedby={describedBy}
                  value={website.maxPagesPerSite ?? 6}
                  onChange={(event) => patchEnrichment({ website: { ...website, maxPagesPerSite: Number(event.target.value) } })}
                />
              )}
            </Field>
            <div className="flex items-end pb-2">
              <Checkbox
                label="Render JavaScript (slower, costs browser time)"
                checked={website.renderJavaScript ?? false}
                onChange={(event) => patchEnrichment({ website: { ...website, renderJavaScript: event.target.checked } })}
              />
            </div>
          </div>
        ) : null}
      </section>

      {/*
        People come from the company's own website — the crawler reads team, leadership and
        "about" pages for a name paired with a role. There is no LinkedIn people search here, so
        this section does not offer one: a toggle that plans work nothing can execute is worse
        than no toggle.
      */}
      <section className="space-y-3">
        <div>
          <h3 className="text-[13px] font-semibold text-foreground">People discovery</h3>
          <p className="text-[13px] text-muted-foreground">
            Named contacts are read from each company&rsquo;s own site — team, leadership and about pages — whenever website
            enrichment is on. LinkedIn is not searched for people; this build has no LinkedIn crawler.
          </p>
        </div>
        {website.enabled === false ? (
          <Alert tone="info">Turn on website enrichment above to collect people.</Alert>
        ) : (
          <Field label="Maximum people per company">
            {({ id }) => (
              <Input
                id={id}
                type="number"
                min={1}
                max={25}
                className="tabular max-w-40"
                value={people.maxPerCompany ?? 5}
                onChange={(event) => patchEnrichment({ people: { ...people, maxPerCompany: Number(event.target.value) } })}
              />
            )}
          </Field>
        )}
      </section>

      <section className="space-y-3">
        <div>
          <h3 className="text-[13px] font-semibold text-foreground">Duplicate merging</h3>
          <p className="text-[13px] text-muted-foreground">
            A false merge loses data that is hard to recover, so pairs below the auto-merge score are queued for review instead
            of being merged silently.
          </p>
        </div>
        {errors['reviewThreshold'] ? <Alert tone="error">{errors['reviewThreshold']}</Alert> : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Merge automatically at or above" hint="0.80 – 1.00">
            {({ id, describedBy }) => (
              <Input
                id={id}
                type="number"
                min={0.8}
                max={1}
                step={0.01}
                className="tabular"
                aria-describedby={describedBy}
                value={rules.autoMergeThreshold ?? 0.9}
                onChange={(event) => patchRules({ autoMergeThreshold: Number(event.target.value) })}
              />
            )}
          </Field>
          <Field label="Queue for review at or above" hint="Must be below the auto-merge score." error={errors['reviewThreshold']}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                type="number"
                min={0.5}
                max={0.95}
                step={0.01}
                className="tabular"
                invalid={invalid}
                aria-describedby={describedBy}
                value={rules.reviewThreshold ?? 0.7}
                onChange={(event) => patchRules({ reviewThreshold: Number(event.target.value) })}
              />
            )}
          </Field>
        </div>
      </section>
    </div>
  );
}
